import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from './Layout';
import { Img } from './Img';
import { ReadyToBidBanner } from './ReadyToBidBanner';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { colors } from '../constants/colors';
import { parseUTC } from '../utils/date';
import { Tv } from 'lucide-react';
import type { Show } from '../types';
import { UserAvatar } from './UserAvatar';

import { getDataApiUrl } from '../config/env';

const CATEGORIES = [
  { id: 'all', label: 'All' },
  { id: 'collectibles', label: 'Collectibles' },
  { id: 'fashion', label: 'Fashion' },
  { id: 'electronics', label: 'Electronics' },
  { id: 'sports', label: 'Sports' },
  { id: 'home', label: 'Home' },
  { id: 'art', label: 'Art' },
  { id: 'vintage', label: 'Vintage' },
];

function formatTimeUntil(scheduledAt: string): string {
  const now = new Date();
  const scheduled = parseUTC(scheduledAt);
  const diffMs = scheduled.getTime() - now.getTime();

  if (diffMs < 0) return 'Starting soon';

  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays > 0) return `In ${diffDays}d`;
  if (diffHours > 0) return `In ${diffHours}h`;
  if (diffMins > 0) return `In ${diffMins}m`;
  return 'Starting soon';
}

interface ShowCardProps {
  show: Show;
  onClick: () => void;
}

function ShowCard({ show, onClick }: ShowCardProps) {
  const isLive = show.status === 'LIVE';
  const sellerName = show.sellerUsername || show.sellerId.slice(0, 8);
  const [hovered, setHovered] = useState(false);

  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        ...styles.showCard,
        ...(hovered ? styles.showCardHover : {}),
      }}
    >
      <div style={styles.thumbnailContainer}>
        <Img
          src={show.thumbnailUrl}
          alt={show.name}
          variant="thumb"
          renderedWidth={280}
          blurhash={show.blurhash}
          width={show.imageWidth ?? undefined}
          height={show.imageHeight ?? undefined}
          style={styles.thumbnailImg}
          onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
        />

        {/* Status badge */}
        {isLive ? (
          <div style={styles.liveBadge}>
            <span style={styles.liveDot} />
            <span style={styles.badgeText}>LIVE</span>
          </div>
        ) : (
          <div style={styles.scheduledBadge}>
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              style={{ marginRight: 4 }}
            >
              <circle cx="12" cy="12" r="10" />
              <polyline points="12 6 12 12 16 14" />
            </svg>
            <span style={styles.badgeText}>{formatTimeUntil(show.scheduledAt)}</span>
          </div>
        )}

        {/* Gradient overlay with show name */}
        <div style={styles.gradientOverlay}>
          <p style={styles.showName}>{show.name}</p>
        </div>
      </div>

      {/* Seller info */}
      <div style={styles.sellerInfo}>
        <UserAvatar src={show.sellerAvatarUrl} blurhash={show.sellerAvatarBlurhash} alt={sellerName} size={28} />
        <span style={styles.sellerName}>{sellerName}</span>
      </div>
    </button>
  );
}

export function ShowsList() {
  useDocumentTitle();
  const [liveShows, setLiveShows] = useState<Show[]>([]);
  const [scheduledShows, setScheduledShows] = useState<Show[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const fetchShows = async () => {
      try {
        const [liveRes, scheduledRes] = await Promise.all([
          fetch(`${getDataApiUrl()}/api/v1/shows?status=LIVE`),
          fetch(`${getDataApiUrl()}/api/v1/shows?status=SCHEDULED`),
        ]);

        if (liveRes.ok) {
          const data = await liveRes.json();
          setLiveShows(data);
        }
        if (scheduledRes.ok) {
          const data = await scheduledRes.json();
          setScheduledShows(data);
        }
      } catch (e) {
        console.error('Failed to fetch shows:', e);
      } finally {
        setLoading(false);
      }
    };

    fetchShows();
    const interval = setInterval(fetchShows, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleJoinShow = (show: Show) => {
    navigate(`/watch/${show.id}`);
  };

  const filterShows = (shows: Show[]) => {
    return shows.filter((show) => {
      const matchesCategory =
        selectedCategory === 'all' ||
        show.categories?.includes(selectedCategory);
      const matchesSearch =
        !searchQuery ||
        show.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        show.sellerUsername?.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesCategory && matchesSearch;
    });
  };

  const filteredLiveShows = filterShows(liveShows);
  const filteredScheduledShows = filterShows(scheduledShows);
  const hasNoShows = filteredLiveShows.length === 0 && filteredScheduledShows.length === 0;

  return (
    <Layout searchQuery={searchQuery} onSearchChange={setSearchQuery}>
      <div style={styles.container}>
        <h1 style={styles.srOnly}>Live Shows on Snagg</h1>
        {/* Category Filters */}
        <div style={styles.filtersSection}>
          <div style={styles.categories}>
            {CATEGORIES.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                style={{
                  ...styles.categoryChip,
                  ...(selectedCategory === category.id ? styles.categoryChipActive : {}),
                }}
              >
                {category.label}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div style={styles.loadingState}>
            <p style={styles.loadingText}>Loading shows...</p>
          </div>
        ) : hasNoShows ? (
          <div style={styles.emptyState}>
            <div style={styles.emptyIcon}>
              <Tv size={48} color={colors.brand.blue} />
            </div>
            <h3 style={styles.emptyTitle}>
              {searchQuery || selectedCategory !== 'all'
                ? 'No matching shows'
                : 'No live shows right now'}
            </h3>
            <p style={styles.emptyText}>
              {searchQuery || selectedCategory !== 'all'
                ? 'Try a different search or clear your filters to see all upcoming shows.'
                : 'New shows are added every day. Check back soon or start selling live!'}
            </p>
            {selectedCategory !== 'all' && (
              <button
                onClick={() => setSelectedCategory('all')}
                style={styles.emptyAction}
              >
                Show all
              </button>
            )}
          </div>
        ) : (
          <div style={styles.content}>
            <ReadyToBidBanner />
            {filteredLiveShows.length > 0 && (
              <section style={styles.section}>
                <div style={styles.sectionHeader}>
                  <h2 style={styles.sectionTitle}>Live Now</h2>
                  <span style={styles.showCount}>{filteredLiveShows.length} shows</span>
                </div>
                <div style={styles.grid}>
                  {filteredLiveShows.map((show) => (
                    <ShowCard key={show.id} show={show} onClick={() => handleJoinShow(show)} />
                  ))}
                </div>
              </section>
            )}

            {filteredScheduledShows.length > 0 && (
              <section style={styles.section}>
                <div style={styles.sectionHeader}>
                  <h2 style={styles.sectionTitle}>Coming Up</h2>
                  <span style={styles.showCount}>{filteredScheduledShows.length} shows</span>
                </div>
                <div style={styles.grid}>
                  {filteredScheduledShows.map((show) => (
                    <ShowCard key={show.id} show={show} onClick={() => handleJoinShow(show)} />
                  ))}
                </div>
              </section>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    padding: '16px 16px 24px',
    maxWidth: '1200px',
    margin: '0 auto',
  },
  srOnly: {
    position: 'absolute',
    width: '1px',
    height: '1px',
    padding: 0,
    margin: '-1px',
    overflow: 'hidden',
    clip: 'rect(0, 0, 0, 0)',
    whiteSpace: 'nowrap',
    borderWidth: 0,
  },
  filtersSection: {
    marginBottom: '24px',
  },
  categories: {
    display: 'flex',
    gap: '8px',
    overflowX: 'auto',
    paddingBottom: '4px',
    scrollbarWidth: 'none',
    msOverflowStyle: 'none',
    WebkitOverflowScrolling: 'touch',
    maskImage: 'linear-gradient(to right, black calc(100% - 32px), transparent)',
    WebkitMaskImage: 'linear-gradient(to right, black calc(100% - 32px), transparent)',
  } as React.CSSProperties,
  categoryChip: {
    padding: '10px 16px',
    fontSize: '14px',
    fontWeight: 500,
    color: colors.gray[600],
    backgroundColor: colors.white,
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: colors.gray[300],
    borderRadius: '100px',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    transition: 'color 0.2s, background-color 0.2s, border-color 0.2s',
    minHeight: '44px',
  },
  categoryChipActive: {
    color: colors.white,
    backgroundColor: colors.brand.blue,
    borderColor: colors.brand.blue,
  },
  loadingState: {
    textAlign: 'center',
    padding: '64px 24px',
  },
  loadingText: {
    color: colors.gray[600],
    fontSize: '15px',
  },
  emptyState: {
    textAlign: 'center',
    padding: '64px 24px',
    backgroundColor: colors.white,
    borderRadius: '16px',
    border: `1px solid ${colors.gray[300]}`,
  },
  emptyIcon: {
    marginBottom: '16px',
  },
  emptyTitle: {
    fontSize: '18px',
    fontWeight: 600,
    color: colors.ink.black,
    marginBottom: '8px',
  },
  emptyText: {
    fontSize: '14px',
    color: colors.gray[600],
    maxWidth: '320px',
    margin: '0 auto',
    lineHeight: 1.5,
  },
  emptyAction: {
    marginTop: '16px',
    padding: '10px 24px',
    fontSize: '14px',
    fontWeight: 600,
    color: colors.white,
    backgroundColor: colors.brand.blue,
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
  },
  content: {},
  section: {
    marginBottom: '32px',
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '16px',
  },
  sectionTitle: {
    fontSize: '20px',
    fontWeight: 700,
    color: colors.ink.black,
    margin: 0,
    letterSpacing: '-0.01em',
  },
  showCount: {
    fontSize: '14px',
    color: colors.gray[600],
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
    gap: '12px',
  },
  showCard: {
    backgroundColor: colors.white,
    borderRadius: '16px',
    overflow: 'hidden',
    border: 'none',
    cursor: 'pointer',
    textAlign: 'left',
    padding: 0,
    transition: 'transform 0.2s, box-shadow 0.2s',
  },
  showCardHover: {
    transform: 'translateY(-4px)',
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
  },
  thumbnailContainer: {
    width: '100%',
    aspectRatio: '3/4',
    position: 'relative',
    backgroundColor: colors.gray[100],
    overflow: 'hidden',
  },
  thumbnailImg: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  liveBadge: {
    position: 'absolute',
    top: '8px',
    left: '8px',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    padding: '4px 8px',
    backgroundColor: colors.accent.pink,
    borderRadius: '999px',
  },
  liveDot: {
    width: '8px',
    height: '8px',
    backgroundColor: '#FFFFFF',
    borderRadius: '50%',
  },
  scheduledBadge: {
    position: 'absolute',
    top: '8px',
    left: '8px',
    display: 'flex',
    alignItems: 'center',
    padding: '4px 8px',
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    borderRadius: '999px',
    color: '#FFFFFF',
  },
  badgeText: {
    color: '#FFFFFF',
    fontSize: '12px',
    fontWeight: 600,
  },
  gradientOverlay: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: '80px',
    background: 'linear-gradient(to top, rgba(0,0,0,0.7), transparent)',
    display: 'flex',
    alignItems: 'flex-end',
    padding: '8px',
  },
  showName: {
    color: '#FFFFFF',
    fontSize: '14px',
    fontWeight: 600,
    margin: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
  },
  sellerInfo: {
    display: 'flex',
    alignItems: 'center',
    padding: '8px',
    gap: '8px',
  },
  sellerAvatar: {
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    objectFit: 'cover',
  },
  sellerAvatarPlaceholder: {
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    backgroundColor: colors.brand.blue,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  sellerInitial: {
    fontSize: '12px',
    fontWeight: 600,
    color: colors.white,
  },
  sellerName: {
    fontSize: '14px',
    fontWeight: 500,
    color: colors.ink.black,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
};
